import React, { useMemo } from 'react';
import { Chart } from 'primereact/chart';
import { Tooltip } from 'react-tooltip';
import { SkillFrequencyChartProps } from '../types';

export const SkillFrequencyChart: React.FC<SkillFrequencyChartProps> = ({ data, isLoading = false, tooltip }) => {
    const chartData = useMemo(() => ({
        labels: data.map((item) => item.name),
        datasets: [
            {
                data: data.map((item) => item.percentage), // use percentage for pie
                backgroundColor: data.map((item) => item.color),
                hoverBackgroundColor: data.map((item) => item.color),
            },
        ],
    }), [data]);

    const chartOptions = useMemo(() => ({
        responsive: false,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context: any) => ` ${context.label}: ${context.parsed}%`,
                },
            },
            datalabels: {
                color: '#ffffff',
                font: {
                    size: 13,
                    weight: 'bold' as const,
                },
                formatter: (value: any) => {
                    if (value < 10) return null;
                    return `${value}%`;
                },
            },
        },
    }), []);

    return (
        <div className="cap-skill-chart">
            <div className="cap-stat-card__label-row">
                <h3 className="cap-skill-chart__title">Skill Coaching Frequency</h3>
                {tooltip && (
                    <>
                        <span
                            className="cap-stat-card__tooltip-icon"
                            data-tooltip-id="stat-card-skill-frequency-chart"
                        >
                            ?
                        </span>
                        <Tooltip
                            id="stat-card-skill-frequency-chart"
                            className="cap-stat-card__tooltip"
                            place="top"
                        >
                            <div style={{ maxWidth: '250px', fontSize: '12px', lineHeight: '1.5' }}>
                                {tooltip}
                            </div>
                        </Tooltip>
                    </>
                )}
            </div>
            <hr className="cap-circular-metric__divider" />
            {isLoading ? (
                <div className="cap-skill-chart__loading">
                    <div className="cap-skill-chart__skeleton-circle" />
                    <div className="cap-skill-chart__loading-legend">
                        <p className="cap-skill-chart__loading-text">Some data may take some time to update</p>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginTop: '20px' }}>
                    {/* Pie Chart */}
                    <div style={{ width: '250px', height: '250px', flexShrink: 0 }}>
                        <Chart
                            type="pie"
                            data={chartData}
                            options={chartOptions}
                            width="250"
                            height="250"
                        />
                    </div>

                    {/* Custom Table Legend */}
                    {data.length > 0 && (
                        <div className="cap-skill-chart__table">
                            <div className="cap-skill-chart__table-header">
                                <span>Skills</span>
                                <span>Count</span>
                            </div>
                            {data.map((item, index) => (
                                <div key={index} className="cap-skill-chart__table-row">
                                    <div className="cap-skill-chart__table-label">
                                        <span
                                            className="cap-skill-chart__table-dot"
                                            style={{ backgroundColor: item.color }}
                                        />
                                        <span>{item.name}</span>
                                    </div>
                                    <span className="cap-skill-chart__table-count">{item.count}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};